import AddIcon from '@mui/icons-material/Add';
import BookmarksOutlinedIcon from '@mui/icons-material/BookmarksOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import type { AddressType, SavedAddress } from '@/types/parcel/userData';

import SavedAddressFormModal from './saved-address-form-modal';
import useSavedAddressesModal from './use-saved-addresses-modal';

interface SavedAddressesModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (address: SavedAddress) => void;
  targetType?: AddressType;
  userCountry?: string;
}

const getTypeChip = (type: SavedAddress['type']) => {
  switch (type) {
    case 'origin':
      return <Chip label="Origen" size="small" color="primary" variant="outlined" />;
    case 'destination':
      return <Chip label="Destino" size="small" color="secondary" variant="outlined" />;
    case 'both':
    default:
      return (
        <Chip
          label="Ambos"
          size="small"
          sx={{ backgroundColor: '#E8F5E9', color: '#2E7D32', borderColor: '#A5D6A7' }}
          variant="outlined"
        />
      );
  }
};

export const SavedAddressesModal = (props: SavedAddressesModalProps) => {
  const { open, onClose, onSelect, targetType, userCountry = 'MEX' } = props;

  const hooks = useSavedAddressesModal({
    open,
    targetType,
    onSelect,
    onClose,
  });

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        {/* Cabecera del Modal */}
        <DialogTitle
          sx={{
            m: 0,
            p: 2.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div className="flex flex-row items-center gap-2.5">
            <BookmarksOutlinedIcon sx={{ color: 'primary.main', fontSize: '1.75rem' }} />
            <div>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {targetType === 'origin'
                  ? 'Direcciones de Recolección (Origen)'
                  : targetType === 'destination'
                    ? 'Destinatarios y Direcciones Guardadas'
                    : 'Libreta de Direcciones'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Selecciona una dirección frecuente para autocompletar tu envío
              </Typography>
            </div>
          </div>
          <IconButton aria-label="close" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Banner de Vigencia de 7 Días */}
          <Alert
            severity="info"
            variant="outlined"
            sx={{ borderRadius: '8px', fontSize: '0.875rem' }}
          >
            <strong>Vigencia de 7 días:</strong> Las direcciones permanecen guardadas durante 7 días
            a partir de su último uso. Cada vez que utilizas o editas una dirección, su vigencia se
            reinicia automáticamente por 7 días más.
          </Alert>

          {/* Barra de búsqueda y botón Nueva Dirección */}
          <div className="flex flex-row justify-between items-center gap-3 flex-wrap sm:flex-nowrap">
            <TextField
              size="small"
              placeholder="Buscar por alias, calle, colonia, contacto o teléfono..."
              value={hooks.searchQuery}
              onChange={(e) => hooks.setSearchQuery(e.target.value)}
              fullWidth
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={hooks.handleOpenCreate}
              sx={{
                whiteSpace: 'nowrap',
                textTransform: 'none',
                fontWeight: 600,
                minWidth: '170px',
              }}
            >
              Nueva Dirección
            </Button>
          </div>

          {/* Pestañas de filtrado (si no viene fijo por targetType) */}
          {!targetType && (
            <Tabs
              value={hooks.selectedTab}
              onChange={(_, val) => hooks.setSelectedTab(val)}
              variant="standard"
              sx={{ borderBottom: 1, borderColor: 'divider', minHeight: '38px' }}
            >
              <Tab
                label="Todas"
                value="all"
                sx={{ textTransform: 'none', fontWeight: 600, py: 0.5, minHeight: '38px' }}
              />
              <Tab
                label="Origen (Recolección)"
                value="origin"
                sx={{ textTransform: 'none', fontWeight: 600, py: 0.5, minHeight: '38px' }}
              />
              <Tab
                label="Destino (Entrega)"
                value="destination"
                sx={{ textTransform: 'none', fontWeight: 600, py: 0.5, minHeight: '38px' }}
              />
            </Tabs>
          )}

          {/* Tabla de Direcciones Guardadas */}
          {hooks.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress size={36} />
            </Box>
          ) : hooks.addresses.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
                gap: 1.5,
              }}
            >
              <BookmarksOutlinedIcon sx={{ fontSize: '3.5rem', color: '#B0BEC5' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                {hooks.searchQuery
                  ? 'No se encontraron direcciones que coincidan con la búsqueda'
                  : 'No tienes direcciones guardadas aún'}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'text.disabled', textAlign: 'center', maxWidth: '400px' }}
              >
                Guarda tus direcciones y contactos frecuentes para agilizar la creación de tus
                próximos envíos.
              </Typography>
              {!hooks.searchQuery && (
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={hooks.handleOpenCreate}
                  sx={{ mt: 1, textTransform: 'none' }}
                >
                  Registrar mi primera dirección
                </Button>
              )}
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ borderRadius: '8px', maxHeight: '440px' }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, backgroundColor: '#F8FAFC' } }}>
                    <TableCell>Alias / Tipo</TableCell>
                    <TableCell>Dirección Completa</TableCell>
                    <TableCell>Contacto</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {hooks.addresses.map((address) => {
                    const fullStreet = [
                      address.address,
                      address.street_number ? `No. ${address.street_number}` : '',
                      address.interior_number ? `Int. ${address.interior_number}` : '',
                    ]
                      .filter(Boolean)
                      .join(' ');

                    const fullLocation = [
                      address.colony,
                      address.zip_code ? `C.P. ${address.zip_code}` : '',
                      address.city,
                      address.state,
                      address.country,
                    ]
                      .filter(Boolean)
                      .join(', ');

                    const contactName =
                      `${address.contact.first_name || ''} ${address.contact.last_name || ''}`.trim();

                    return (
                      <TableRow
                        key={address.id}
                        hover
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        {/* Columna: Alias / Tipo */}
                        <TableCell sx={{ minWidth: '160px' }}>
                          <div className="flex flex-col gap-1 items-start">
                            <span className="font-semibold text-gray-900 text-sm">
                              {address.alias || 'Sin alias'}
                            </span>
                            {getTypeChip(address.type)}
                          </div>
                        </TableCell>

                        {/* Columna: Dirección */}
                        <TableCell sx={{ minWidth: '240px', maxWidth: '360px' }}>
                          <div className="flex flex-col text-xs">
                            <div className="flex items-center gap-1 font-medium text-gray-800">
                              <LocationOnOutlinedIcon
                                sx={{ fontSize: '0.95rem', color: 'primary.main' }}
                              />
                              <span>{fullStreet}</span>
                            </div>
                            <span className="text-gray-500 ml-4 mt-0.5">{fullLocation}</span>
                            {address.references && (
                              <span className="text-gray-400 italic ml-4 mt-0.5">
                                Ref: {address.references}
                              </span>
                            )}
                          </div>
                        </TableCell>

                        {/* Columna: Contacto */}
                        <TableCell sx={{ minWidth: '180px' }}>
                          <div className="flex flex-col text-xs gap-0.5">
                            {contactName && (
                              <div className="flex items-center gap-1 font-medium text-gray-800">
                                <PersonOutlineIcon
                                  sx={{ fontSize: '0.9rem', color: 'text.secondary' }}
                                />
                                <span>{contactName}</span>
                              </div>
                            )}
                            {address.contact.phone && (
                              <div className="flex items-center gap-1 text-gray-600">
                                <PhoneOutlinedIcon
                                  sx={{ fontSize: '0.9rem', color: 'text.secondary' }}
                                />
                                <span>{address.contact.phone}</span>
                              </div>
                            )}
                            {address.contact.email && (
                              <span className="text-gray-400 truncate max-w-[180px]">
                                {address.contact.email}
                              </span>
                            )}
                          </div>
                        </TableCell>

                        {/* Columna: Acciones */}
                        <TableCell align="center" sx={{ minWidth: '150px' }}>
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => hooks.handleSelectAddress(address)}
                              sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                py: 0.3,
                                px: 1.5,
                                borderRadius: '6px',
                              }}
                            >
                              Usar
                            </Button>
                            <Tooltip title="Editar dirección">
                              <IconButton
                                size="small"
                                onClick={() => hooks.handleOpenEdit(address)}
                                sx={{ color: 'text.secondary', p: 0.5 }}
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar dirección">
                              <IconButton
                                size="small"
                                onClick={() => hooks.handleOpenDelete(address)}
                                sx={{ color: 'error.main', p: 0.5 }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button variant="outlined" onClick={onClose}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sub-modal de Creación y Edición */}
      {hooks.isFormOpen && (
        <SavedAddressFormModal
          open={hooks.isFormOpen}
          onClose={hooks.handleCloseForm}
          onSave={hooks.handleSaveForm}
          initialAddress={hooks.editingAddress}
          defaultType={hooks.defaultType}
          defaultCountry={userCountry}
        />
      )}

      {/* Diálogo de Confirmación de Eliminación */}
      <Dialog open={!!hooks.deletingAddress} onClose={hooks.handleCloseDelete}>
        <DialogTitle sx={{ fontWeight: 700 }}>¿Eliminar dirección guardada?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que deseas eliminar{' '}
            <strong>{hooks.deletingAddress?.alias || 'esta dirección'}</strong>? Esta acción no se
            puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button variant="outlined" onClick={hooks.handleCloseDelete} disabled={hooks.isDeleting}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={hooks.handleConfirmDelete}
            disabled={hooks.isDeleting}
          >
            {hooks.isDeleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SavedAddressesModal;
